import { useMemo, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GlassCard } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import type { RoleTable } from '@/versions/v1/schema/plant-role';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { vizToneColor, type VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import { ActorChip } from '@/versions/v1/modules/advisory/PipelineStrip';
import { GATE_APPROVED_SURFACE, GATE_PRIMARY_BUTTON, gatePillClass } from '@/versions/v1/modules/advisory/gate-ui';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { AskAssistant } from '@/versions/v1/shared/AskAssistant';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import type { RosterPeriodId } from '@/versions/v1/shared/roster-period.derive';
import { LandingStripTabs } from './GmLeadBand';
import { GmCard, statePillClass } from './GmTabs';
import { DecisionField, NEUTRAL_PILL } from './LandingDecisionGate';
import type { PerformanceInput } from './Performance.transform';
import {
  croActions,
  croActivePeriod,
  croAlarms,
  croChecks,
  croCircuitRead,
  croGuardrailLine,
  croHeader,
  croLoops,
  croOnwardCard,
  croPayableLevers,
  croPeriods,
  croSince,
  croStatusBand,
  croStripCells,
  croThroughputGap,
  croVariables,
  croZnInsight,
  type CroAction,
  type CroBandCell,
  type CroCircuitRead,
  type CroLever,
  type CroTabId,
  type CroVariableStatus,
} from './ControlRoomLanding.transform';

type OpenCase = (caseId: string) => void;

const CAPS = 'text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted';
const HAIRLINE_ROW = 'border-b border-[var(--color-divider)] last:border-b-0';
const CODE_LINK =
  'whitespace-nowrap font-mono text-[10.5px] tracking-[0.02em] text-ink-primary underline decoration-[var(--hairline-strong)] underline-offset-2 hover:decoration-ink-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]';

/** A soft status pill on the state tokens (comp `.pill`); `neutral` is the quiet pill. */
function pill(tone: VizTone): string {
  return tone === 'bad' || tone === 'warn' || tone === 'info' ? statePillClass(tone) : NEUTRAL_PILL;
}

// ---- The header row --------------------------------------------------------------------

function HeaderRow({
  state,
  periodId,
  onPeriod,
}: {
  state: SessionState;
  periodId: RosterPeriodId;
  onPeriod: (id: RosterPeriodId) => void;
}) {
  const { periods, tooltip } = croPeriods(state);
  const period = croActivePeriod(state, periodId);
  const header = croHeader(state, period);
  return (
    <div className="-mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1" data-cro-header>
      <p className="text-sm text-muted">{header.subtitle}</p>
      <div className="flex flex-wrap items-center gap-2">
        {header.sampleLine ? <span className="text-xs tabular-nums text-muted" data-cro-sample-line>{header.sampleLine}</span> : null}
        <ProvenanceChip text="[syn]" title={tooltip} compact />
        {periods.length > 1 ? (
          <div
            role="group"
            aria-label="Period"
            className="inline-flex gap-0.5 rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-1)] p-0.5"
          >
            {periods.map((row) => {
              const active = row.id === period?.id;
              return (
                <button
                  key={row.id}
                  type="button"
                  aria-pressed={active}
                  data-cro-period={row.id}
                  onClick={() => onPeriod(row.id)}
                  className={cn(
                    'rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]',
                    active ? 'bg-ink-primary text-[var(--surface-1)]' : 'text-ink-secondary hover:text-ink-primary',
                  )}
                >
                  {row.label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---- The lead band ---------------------------------------------------------------------

function OnwardCard({ state, href }: { state: SessionState; href: string }) {
  const advisory = useAdvisory();
  const card = croOnwardCard(state, advisory);
  return (
    <Link
      to={href}
      data-cro-onward
      aria-label={`Awaiting my action · ${card.count} ${card.count === 1 ? 'case' : 'cases'} · open Advisory`}
      className="col-span-12 flex min-h-[7.5rem] min-w-0 flex-col gap-1 rounded-2xl border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-4 py-3 transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)] lg:col-span-3"
    >
      <span className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">Awaiting my action</span>
      <span className="flex flex-wrap items-baseline gap-x-1.5 text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">
        <span data-cro-onward-figure>{card.count}</span>
        <span className="text-xs font-semibold tracking-normal text-muted">{card.count === 1 ? 'case' : 'cases'}</span>
      </span>
      <span className="text-xs leading-snug tabular-nums text-muted" data-cro-onward-comparison>
        <span className="font-semibold text-ink-secondary">{card.lead}</span>
        {card.first ? (
          <>
            {' · first: '}
            <span className="whitespace-nowrap">{card.first.name}</span>
            {card.first.clock ? `, ${card.first.clock}` : null}
          </>
        ) : null}
      </span>
      {card.loopLine ? (
        <span className="text-xs leading-snug tabular-nums text-muted" data-cro-onward-loops>
          {card.loopLine}
        </span>
      ) : null}
      <span className="mt-auto inline-flex items-center gap-1 pt-1 text-[12.5px] font-semibold text-ink-primary">
        Advisory
        <ArrowUpRight className="size-3.5" aria-hidden="true" />
      </span>
    </Link>
  );
}

function LeadBand({
  state,
  tab,
  onTabChange,
  input,
  table,
  periodId,
  advisoryHref,
}: {
  state: SessionState;
  tab: CroTabId;
  onTabChange: (tab: CroTabId) => void;
  input: PerformanceInput;
  table: RoleTable | undefined;
  periodId: RosterPeriodId;
  advisoryHref: string;
}) {
  const advisory = useAdvisory();
  const cells = croStripCells(state, advisory, input, table, croActivePeriod(state, periodId));
  return (
    <Commentable name="Kpis">
      <div className="grid grid-cols-12 gap-3" data-cro-lead-band>
        <GlassCard
          as="nav"
          tone="matte"
          aria-label="Landing views"
          className="col-span-12 overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0 lg:col-span-9"
        >
          <LandingStripTabs
            cells={cells}
            selected={tab}
            onSelect={onTabChange}
            dataPrefix="cro"
            panelId="cro-landing-panel"
            columnsClassName="sm:grid-cols-4"
          />
        </GlassCard>
        <OnwardCard state={state} href={advisoryHref} />
      </div>
    </Commentable>
  );
}

// ---- The status band -------------------------------------------------------------------

/** A recovery's seven daily points over its design line (comp `.spark`): the
 *  line in the cell's state tone, the design dashed and named, the last point dotted. */
function Spark({ points, design, tone }: { points: readonly number[]; design: number; tone: VizTone }) {
  const w = 112;
  const h = 44;
  const pad = { l: 2, r: 36, t: 6, b: 6 };
  const all = [...points, design];
  const span = Math.max(...all) - Math.min(...all) || 1;
  const lo = Math.min(...all) - span * 0.12;
  const hi = Math.max(...all) + span * 0.12;
  const x = (i: number) => pad.l + ((w - pad.l - pad.r) * i) / Math.max(points.length - 1, 1);
  const y = (v: number) => pad.t + (h - pad.t - pad.b) * (1 - (v - lo) / (hi - lo));
  const d = points.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join('');
  const colour = vizToneColor(tone);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} role="img" aria-label="7-day trend against design" className="shrink-0" data-cro-spark>
      <line x1={pad.l} x2={w - pad.r} y1={y(design)} y2={y(design)} stroke="var(--ink-tertiary)" strokeDasharray="3 3" />
      <text x={w - pad.r + 4} y={y(design) + 3} fontSize={10} fill="var(--ink-tertiary)">
        design
      </text>
      <path d={d} fill="none" stroke={colour} strokeWidth={1.6} strokeLinejoin="round" />
      <circle cx={x(points.length - 1)} cy={y(points[points.length - 1] ?? design)} r={2.6} fill={colour} />
    </svg>
  );
}

function BandCell({ cell }: { cell: CroBandCell }) {
  return (
    <div
      className="flex min-w-0 flex-col gap-px border-b border-[var(--hairline)] px-3.5 py-2.5 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0"
      data-cro-band-cell={cell.id}
    >
      <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.07em] text-muted">
        <span aria-hidden="true" className="inline-block size-[7px] shrink-0 rounded-full" style={{ backgroundColor: vizToneColor(cell.tone) }} />
        <span className="truncate">{cell.label}</span>
      </span>
      {/* Wide: the text column beside the trend mark (the comp). Narrower than
          1400 px the mark rides beside the figure and the comparison takes the
          cell's full width, so it never folds into four lines. */}
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-2.5">
        <span className="col-start-1 row-start-1 self-end text-[17px] font-semibold leading-tight tabular-nums text-ink-primary" data-cro-band-figure={cell.id}>
          {cell.value}
          {cell.unit ? <span className="ml-1 text-xs font-semibold text-muted">{cell.unit}</span> : null}
        </span>
        {cell.spark ? (
          <div className="col-start-2 row-start-1 min-[1400px]:row-span-3">
            <Spark points={cell.spark.points} design={cell.spark.design} tone={cell.tone} />
          </div>
        ) : null}
        <p className="col-span-2 text-xs leading-snug tabular-nums text-muted min-[1400px]:col-span-1 min-[1400px]:col-start-1" data-cro-band-comparison={cell.id}>
          <span className="font-semibold text-ink-secondary">{cell.captionLead}</span>
          {cell.caption}
        </p>
        {cell.sub ? (
          <p className="col-span-2 text-xs leading-snug tabular-nums text-muted min-[1400px]:col-span-1 min-[1400px]:col-start-1">{cell.sub}</p>
        ) : null}
      </div>
    </div>
  );
}

function StatusBand({ state }: { state: SessionState }) {
  const cells = croStatusBand(state);
  return (
    <div className="card-matte-2 grid grid-cols-1 rounded-2xl sm:grid-cols-2 xl:grid-cols-4" data-cro-band>
      {cells.map((cell) => (
        <BandCell key={cell.id} cell={cell} />
      ))}
    </div>
  );
}

// ---- The circuit read ------------------------------------------------------------------

function LeverChipView({ lever, onOpenCase }: { lever: CroLever; onOpenCase: OpenCase }) {
  return (
    <span
      title={lever.title}
      data-cro-lever={lever.key}
      data-cro-lever-off={lever.off ? 'true' : undefined}
      className={cn(
        'inline-flex items-baseline gap-1 whitespace-nowrap rounded-lg border px-[7px] py-px text-[11.5px] tabular-nums',
        lever.off
          ? 'border-[color-mix(in_srgb,var(--state-warning)_45%,transparent)] bg-[color-mix(in_srgb,var(--state-warning)_11%,transparent)] text-ink-primary'
          : 'border-[var(--hairline-strong)] bg-[var(--surface-2)] text-ink-secondary',
      )}
    >
      {lever.tag ? <span className="font-mono text-[10.5px] text-muted">{lever.tag}</span> : null}
      <span>
        {lever.face} ·{' '}
        {lever.off ? <b className="font-semibold text-[var(--state-warning)]">{lever.comparison}</b> : lever.comparison}
      </span>
      {lever.caseCode ? (
        <button
          type="button"
          className={CODE_LINK}
          data-cro-lever-case={lever.caseCode.code}
          onClick={() => onOpenCase(lever.caseCode!.caseId)}
        >
          {lever.caseCode.code}
        </button>
      ) : null}
    </span>
  );
}

function CircuitRows({ read, onOpenCase }: { read: CroCircuitRead; onOpenCase: OpenCase }) {
  return (
    <div>
      {read.stages.map((stage) => (
        <div
          key={stage.id}
          className={cn('grid grid-cols-[92px_84px_minmax(0,1fr)] items-start gap-x-3 gap-y-1 py-1.5 min-[1400px]:grid-cols-[104px_96px_minmax(0,1fr)]', HAIRLINE_ROW)}
          data-cro-stage={stage.id}
        >
          <span className="text-[13px] font-semibold text-ink-primary">{stage.label}</span>
          <span className="text-right text-[13px] font-semibold tabular-nums text-ink-primary">
            {stage.grade}
            <small className="block text-[10.5px] font-medium text-muted">{stage.gradeSub}</small>
          </span>
          <div className="flex min-w-0 flex-wrap gap-[5px]">
            {stage.levers.map((lever) => (
              <LeverChipView key={lever.key} lever={lever} onOpenCase={onOpenCase} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function GapLine({ state, input, onOpenCase }: { state: SessionState; input: PerformanceInput; onOpenCase: OpenCase }) {
  const gap = useMemo(() => croThroughputGap(state, input), [state, input]);
  if (!gap) return null;
  return (
    <div className="mt-2.5 border-t border-[var(--hairline)] pt-2.5 text-[12.5px]" data-cro-gap>
      <div className="flex items-start justify-between gap-2.5">
        <span className="font-semibold tabular-nums text-ink-primary">
          Throughput {gap.actual}
          <span className="font-medium text-muted">
            {' · '}
            {gap.lead}
            {gap.rest}
          </span>
        </span>
        <span className="shrink-0">
          <ProvenanceChip text="[syn] est." compact />
        </span>
      </div>
      {gap.causes.length > 0 ? (
        <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11.5px] tabular-nums text-muted">
          {gap.causes.map((cause, index) => (
            <span key={cause.caseId} className="inline-flex items-center gap-1.5">
              {index > 0 ? <span aria-hidden="true">·</span> : null}
              {cause.title} <b className="font-semibold text-ink-primary">−{cause.tdEst} t/d</b>
              <button type="button" className={CODE_LINK} onClick={() => onOpenCase(cause.caseId)}>
                {cause.code}
              </button>
            </span>
          ))}
          {gap.owners.map((owner) => (
            <ActorChip key={owner} actor="Human">
              {owner}
            </ActorChip>
          ))}
        </p>
      ) : null}
    </div>
  );
}

function CircuitReadCard({
  state,
  input,
  periodId,
  onOpenCase,
  withGuardrail = false,
  metaDecode = false,
}: {
  state: SessionState;
  input: PerformanceInput;
  periodId: RosterPeriodId;
  onOpenCase: OpenCase;
  withGuardrail?: boolean;
  metaDecode?: boolean;
}) {
  const read = croCircuitRead(state, input.cases, croActivePeriod(state, periodId));
  const guardrail = withGuardrail ? croGuardrailLine(state) : '';
  return (
    <GmCard
      title="Circuit read — grade and levers"
      meta={metaDecode ? `${read.meta} · off setpoint in the warning wash` : read.meta}
      className="h-full"
      data-cro-circuit
    >
      <CircuitRows read={read} onOpenCase={onOpenCase} />
      {guardrail ? (
        <p className="border-t border-[var(--color-divider)] pt-2 text-xs text-[var(--state-warning)]" data-cro-guardrail>
          {guardrail}
        </p>
      ) : null}
      <GapLine state={state} input={input} onOpenCase={onOpenCase} />
    </GmCard>
  );
}

// ---- Your actions ----------------------------------------------------------------------

function RecordGate({ action }: { action: CroAction }) {
  const advisory = useAdvisory();
  const { can } = useRole();
  const gate = action.gate!;
  const recorded = Boolean(gate.recorded);
  return (
    <div
      className={cn(
        'mt-1.5 flex flex-col gap-1.5 rounded-xl border py-[7px] pl-2.5 pr-2 transition-colors duration-300',
        recorded ? GATE_APPROVED_SURFACE : 'border-[var(--hairline-strong)] bg-[var(--surface-2)]',
      )}
      data-cro-gate={recorded ? 'recorded' : 'open'}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <DecisionField label="Step">
          <span className="pr-1">{gate.step}</span>
        </DecisionField>
        <DecisionField label="By">
          <ActorChip actor="Human">{gate.by}</ActorChip>
        </DecisionField>
        {recorded ? null : can('case:evidence') ? (
          <button
            type="button"
            className={cn(GATE_PRIMARY_BUTTON, 'ml-auto h-[26px] px-2.5')}
            onClick={() => advisory.answerEvidence(gate.evidenceId, gate.step)}
            data-cro-gate-record
          >
            Mark done
          </button>
        ) : (
          <span className="ml-auto px-2 text-xs font-medium text-muted">Waiting on {gate.by}</span>
        )}
      </div>
      {gate.recorded ? (
        <div data-cro-gate-line>
          <span className={gatePillClass('approved')}>{gate.recorded}</span>
        </div>
      ) : null}
    </div>
  );
}

function ActionRow({ action, rank, onOpenCase }: { action: CroAction; rank: number; onOpenCase: OpenCase }) {
  return (
    <div className={cn('grid grid-cols-[22px_minmax(0,1fr)] gap-x-2.5 py-[7px]', HAIRLINE_ROW)} data-cro-action={action.code}>
      <span
        className={cn(
          'pt-px text-center text-xs font-bold tabular-nums',
          rank === 1 ? 'text-[var(--color-accent-500)]' : 'text-muted',
        )}
      >
        {rank}
      </span>
      <div className="min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[13px] font-semibold text-ink-primary" data-cro-action-title>{action.title}</span>
          <span className={pill(action.severity.tone)}>{action.severity.label}</span>
        </div>
        <p className="mt-0.5 text-[12px] font-semibold text-ink-primary" data-cro-action-step>
          {action.step}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <button type="button" className={CODE_LINK} onClick={() => onOpenCase(action.caseId)}>
            {action.code}
          </button>
          {action.who ? <ActorChip actor="Human">{action.who}</ActorChip> : null}
          <span className="text-xs tabular-nums text-muted" data-cro-action-context>
            {action.context}
          </span>
        </div>
        {action.gate ? <RecordGate action={action} /> : null}
      </div>
    </div>
  );
}

function ActionsCard({
  state,
  input,
  onOpenCase,
}: {
  state: SessionState;
  input: PerformanceInput;
  onOpenCase: OpenCase;
}) {
  const advisory = useAdvisory();
  const actions = croActions(state, advisory);
  const checks = croChecks(state, input);
  return (
    <GmCard
      title="Your actions — in order"
      meta={<AskAssistant threadId="thread-adv-pb-recovery" label="Ask about Pb recovery drifting" />}
      className="h-full"
      data-cro-actions
    >
      {actions.map((action, index) => (
        <ActionRow key={action.caseId} action={action} rank={index + 1} onOpenCase={onOpenCase} />
      ))}
      {checks.length > 0 ? (
        <div data-cro-checks>
          <p className={cn('mt-3', CAPS)}>Worth checking or sampling this shift</p>
          {checks.map((check) => (
            <div key={check.text} className={cn('flex justify-between gap-2.5 py-[5px] text-[12.5px] text-ink-secondary', HAIRLINE_ROW)}>
              <span>{check.text}</span>
              <span className="whitespace-nowrap text-xs text-muted">{check.tail}</span>
            </div>
          ))}
        </div>
      ) : null}
    </GmCard>
  );
}

// ---- Views -----------------------------------------------------------------------------

function ShiftView({ state, input, periodId, onOpenCase }: ViewProps) {
  return (
    <div className="space-y-3">
      <StatusBand state={state} />
      <div className="grid grid-cols-12 gap-3">
        <Commentable name="Primary">
          <div className="col-span-12 lg:col-span-7">
            <CircuitReadCard state={state} input={input} periodId={periodId} onOpenCase={onOpenCase} metaDecode />
          </div>
        </Commentable>
        <Commentable name="Secondary">
          <div className="col-span-12 lg:col-span-5">
            <ActionsCard state={state} input={input} onOpenCase={onOpenCase} />
          </div>
        </Commentable>
      </div>
    </div>
  );
}

function CircuitView({ state, input, periodId, onOpenCase }: ViewProps) {
  const insight = croZnInsight(state, input);
  const levers = croPayableLevers(state);
  return (
    <div className="grid grid-cols-12 gap-3">
      <Commentable name="Primary">
        <div className="col-span-12 lg:col-span-7">
          <CircuitReadCard state={state} input={input} periodId={periodId} onOpenCase={onOpenCase} withGuardrail />
        </div>
      </Commentable>
      <Commentable name="Secondary">
        <div className="col-span-12 flex flex-col gap-3 lg:col-span-5">
          {insight ? (
            <GmCard
              title="Why Zn recovery is below design"
              meta={
                <button type="button" className={CODE_LINK} onClick={() => onOpenCase(insight.caseId)}>
                  {insight.code}
                </button>
              }
              data-cro-insight
            >
              <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-2.5 text-[12.5px]">
                <ActorChip actor="Engine" />
                <div className="min-w-0 text-ink-secondary">
                  <p>{insight.body}</p>
                  {insight.pill ? (
                    <p className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span className={statePillClass('warn')}>{insight.pill.label}</span>
                      <span className="text-xs text-muted">{insight.pill.rest}</span>
                    </p>
                  ) : null}
                </div>
              </div>
            </GmCard>
          ) : null}
          {levers.length > 0 ? (
            <GmCard title="Payable metal levers" meta={<ProvenanceChip text="[syn]" compact />} data-cro-levers>
              {levers.map((row) => (
                <div key={row.metal} className={cn('flex justify-between gap-2.5 py-[5px] text-[12.5px] text-ink-secondary', HAIRLINE_ROW)}>
                  <span>
                    <b className="font-semibold text-ink-primary">{row.metal}</b> recovery +1 pt
                  </span>
                  <span className="font-semibold tabular-nums text-ink-primary">{row.value}</span>
                </div>
              ))}
            </GmCard>
          ) : null}
        </div>
      </Commentable>
    </div>
  );
}

const STATUS_PILL: Record<Exclude<CroVariableStatus, 'ok'>, { label: string; tone: VizTone }> = {
  off: { label: 'Off SP', tone: 'warn' },
  low: { label: 'Low', tone: 'bad' },
  high: { label: 'High', tone: 'bad' },
};

const TH = 'whitespace-nowrap border-b border-[var(--color-divider)] px-2 py-1.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted';
const TD = 'border-b border-[var(--color-divider)] px-2 py-[5px] align-middle';

function LoopsView({ state, table }: ViewProps) {
  const loops = croLoops();
  const variables = croVariables(table, state);
  return (
    <div className="grid grid-cols-12 gap-3">
      <Commentable name="ControlLoops">
        <div className="col-span-12 lg:col-span-5">
          <GmCard title="Control loops" meta="mode · time in mode" className="h-full" data-cro-loops>
            {loops.map((loop) => (
              <div key={loop.tag} className={cn('grid grid-cols-[minmax(0,1fr)_auto] gap-x-2.5 gap-y-0.5 py-2 text-[12.5px]', HAIRLINE_ROW)} data-cro-loop={loop.tag}>
                <span className="font-mono text-[11.5px] font-medium text-ink-primary">{loop.tag}</span>
                <span className={pill(loop.tone)}>{loop.modeLabel}</span>
                <span className="col-span-2 text-[11.5px] text-muted">
                  {loop.name}
                  {loop.since ? ` · ${loop.since}` : ''}
                </span>
              </div>
            ))}
          </GmCard>
        </div>
      </Commentable>
      <Commentable name="Primary">
        <div className="col-span-12 lg:col-span-7">
          <GmCard
            flush
            title="Process variables vs setpoint"
            meta="grouped by circuit"
            className="h-full"
            data-cro-variables
          >
            <table className="w-full border-collapse text-[12.5px] tabular-nums">
              <thead>
                <tr>
                  <th className={cn(TH, 'pl-4')}>Variable</th>
                  <th className={cn(TH, 'text-right')}>Reading</th>
                  <th className={cn(TH, 'text-right')}>Setpoint · band</th>
                  <th className={cn(TH, 'pr-4 text-right')}>State</th>
                </tr>
              </thead>
              <tbody>
                {variables.groups.map((group) => (
                  <GroupRows key={group.id} group={group} />
                ))}
              </tbody>
            </table>
          </GmCard>
        </div>
      </Commentable>
    </div>
  );
}

function GroupRows({ group }: { group: ReturnType<typeof croVariables>['groups'][number] }) {
  return (
    <>
      <tr>
        <td colSpan={4} className={cn('border-b border-[var(--color-divider)] bg-[var(--surface-1)] px-4 py-[5px]', CAPS)}>
          {group.label}
        </td>
      </tr>
      {group.rows.map((row) => (
        <tr key={row.id} data-cro-variable={row.id} data-cro-variable-status={row.status}>
          <td className={cn(TD, 'pl-4 font-medium text-ink-primary')}>{row.label}</td>
          <td className={cn(TD, 'text-right font-semibold text-ink-primary')}>{row.reading}</td>
          <td className={cn(TD, 'text-right text-ink-secondary')}>{row.setpoint}</td>
          <td className={cn(TD, 'pr-4 text-right')}>
            {row.status === 'ok' ? (
              <span className="text-xs text-muted">in band</span>
            ) : (
              <span className={pill(STATUS_PILL[row.status].tone)}>{STATUS_PILL[row.status].label}</span>
            )}
          </td>
        </tr>
      ))}
    </>
  );
}

function SinceView({ state, input, periodId, onOpenCase, advisoryHref }: ViewProps) {
  const advisory = useAdvisory();
  const period = croActivePeriod(state, periodId);
  const since = croSince(state, input, period);
  const alarms = croAlarms(state, advisory, period);
  return (
    <div className="grid grid-cols-12 gap-3">
      <Commentable name="Primary">
        <div className="col-span-12 lg:col-span-7">
          <GmCard title={since.title} meta={since.meta} className="h-full" data-cro-brief>
            {since.lines.map((line) => (
              <button
                key={`${line.caseId}-${line.at}`}
                type="button"
                onClick={() => onOpenCase(line.caseId)}
                title={line.outcome}
                className={cn(
                  'grid w-full grid-cols-[44px_minmax(0,1fr)_auto] items-start gap-x-2.5 py-2 text-left text-[12.5px] transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-500)]',
                  HAIRLINE_ROW,
                )}
                data-cro-brief-line={line.caseId}
              >
                <span className="pt-px text-xs tabular-nums text-muted">{line.at}</span>
                <span className="min-w-0">
                  <span className="block font-semibold text-ink-primary">{line.title}</span>
                  <span className={cn('mt-[3px] inline-flex', NEUTRAL_PILL)}>{line.verb}</span>
                </span>
                <span>
                  {line.flag && line.flag !== line.verb ? (
                    <span className={line.flag === 'done' ? gatePillClass('approved') : NEUTRAL_PILL}>{line.flag}</span>
                  ) : null}
                </span>
              </button>
            ))}
            {since.countLine ? (
              <p className="pt-2 text-xs tabular-nums text-muted" data-cro-brief-count>
                {since.countLine}
              </p>
            ) : null}
          </GmCard>
        </div>
      </Commentable>
      <Commentable name="AlarmPanel">
        <div className="col-span-12 lg:col-span-5">
          <GmCard
            title="DCS alarms in the window"
            meta={
              <Link to={advisoryHref} className="inline-flex items-center gap-1 border-b border-[var(--hairline-strong)] text-[12.5px] font-semibold text-ink-primary">
                Advisory
                <ArrowUpRight className="size-3.5" aria-hidden="true" />
              </Link>
            }
            className="h-full"
            data-cro-alarms
          >
            {alarms.rows.map((alarm) => (
              <div key={alarm.id} className={cn('grid grid-cols-[minmax(0,1fr)_auto] gap-x-2.5 gap-y-0.5 py-2 text-[12.5px]', HAIRLINE_ROW)} data-cro-alarm={alarm.code}>
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="whitespace-nowrap font-mono text-[11.5px] font-medium text-ink-primary">{alarm.code}</span>
                  <span className={NEUTRAL_PILL}>{alarm.priority}</span>
                  <span className={alarm.state === 'active' ? statePillClass('warn') : NEUTRAL_PILL}>{alarm.state === 'active' ? 'Active' : 'Acked'}</span>
                </span>
                <span className="text-xs tabular-nums text-muted">{alarm.raised}</span>
                <span className="col-span-2 text-[11.5px] text-muted [text-wrap:pretty]">
                  {alarm.tag && alarm.text.includes(alarm.tag)
                    ? alarm.text.split(alarm.tag).flatMap((part, index) =>
                        index === 0
                          ? [part]
                          : [
                              <span key={index} className="whitespace-nowrap">
                                {alarm.tag}
                              </span>,
                              part,
                            ],
                      )
                    : alarm.text}
                </span>
              </div>
            ))}
            {alarms.hiddenP4 > 0 ? (
              <p className="pt-2 text-xs text-muted" data-cro-alarms-hidden>{`${alarms.hiddenP4} P4 hidden · ${alarms.hiddenP4Codes.join(' · ')}`}</p>
            ) : null}
          </GmCard>
        </div>
      </Commentable>
    </div>
  );
}

type ViewProps = {
  state: SessionState;
  input: PerformanceInput;
  table: RoleTable | undefined;
  periodId: RosterPeriodId;
  onOpenCase: OpenCase;
  advisoryHref: string;
};

// ---- Composition -----------------------------------------------------------------------

/**
 * The Control room landing (polish.P15 #702, locked comp
 * `landing-control-room.html` A, with C's case code on the lever chip and B's
 * context line under each action): the landing family at shift scope. Four
 * strip cells are the tab bar beside Awaiting my action, which opens
 * Advisory; the default view is the status band, the circuit read and the
 * control room's actions in order, the first carrying its Record gate.
 */
export function ControlRoomLanding({
  state,
  projectSlug,
  performanceInput,
  table,
  onOpenCase,
}: {
  state: SessionState;
  projectSlug: string;
  performanceInput: PerformanceInput;
  /** The role seed's key-process-variable table (`dashboardForRole('cro')`). */
  table: RoleTable | undefined;
  onOpenCase: OpenCase;
}) {
  const [tab, setTab] = useState<CroTabId>('shift');
  const [periodId, setPeriodId] = useState<RosterPeriodId>('last-shift');
  const advisoryHref = projectSidebarHref(projectSlug, 'advisory');
  const props: ViewProps = { state, input: performanceInput, table, periodId, onOpenCase, advisoryHref };
  return (
    <div className="space-y-3" data-cro-landing>
      <HeaderRow state={state} periodId={periodId} onPeriod={setPeriodId} />
      <LeadBand
        state={state}
        tab={tab}
        onTabChange={setTab}
        input={performanceInput}
        table={table}
        periodId={periodId}
        advisoryHref={advisoryHref}
      />
      <div id="cro-landing-panel" role="tabpanel" aria-labelledby={`cro-tab-${tab}`}>
        {tab === 'shift' ? <ShiftView {...props} /> : null}
        {tab === 'circuit' ? <CircuitView {...props} /> : null}
        {tab === 'loops' ? <LoopsView {...props} /> : null}
        {tab === 'since' ? <SinceView {...props} /> : null}
      </div>
    </div>
  );
}

